import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import LanguageToggle from '../../components/LanguageToggle';
import {
  Activity, Cpu, CheckCircle, XCircle, Clock,
  RefreshCw, ToggleLeft, ToggleRight, Zap, AlertTriangle,
} from 'lucide-react';
import '../Admin/AdminDashboard.css';
import './AdminAutomation.css';

const JOB_LABELS = {
  quoteAutomation: '🤖 Auto-Quote',
  followUpEngine: '📧 Follow-Up Sequences',
  morningBriefing: '🌅 Morning Briefing',
  anomalyDetection: '🔍 Anomaly Detection',
  seasonalCampaigns: '📅 Seasonal Campaigns',
  paymentTracker: '💰 Payment Tracker',
  weeklyReport: '📊 Weekly Report',
  telegramMarketing: '📱 Telegram Marketing',
  blogGenerator: '✍️ Blog Generator',
  monthlyFinancial: '📈 Monthly Financial',
  customerSegmentation: '👥 Segmentation',
  stockTracker: '📦 Stock Tracker',
  contentGenerator: '🎨 Content Generator',
  reengagementEngine: '💌 Re-engagement',
  pricingIntelligence: '💡 Pricing Intelligence',
};

export default function AdminAutomation() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const toast = useToast();

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/automation/status');
      setStatus(res.data.data ?? res.data);
    } catch {
      toast.error(t('admin.automation.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const toggleAutomation = async () => {
    setToggling(true);
    try {
      await api.patch('/automation/toggle', { module: 'ENABLED', enabled: !status?.enabled });
      toast.success(t('admin.automation.toggleSuccess'));
      fetchStatus();
    } catch {
      toast.error(t('admin.automation.errorToggle'));
    } finally {
      setToggling(false);
    }
  };

  /* ── derived counts ── */
  const successCount = status?.jobs?.filter(j => j.lastStatus === 'success').length ?? 0;
  const errorCount   = status?.jobs?.filter(j => j.lastStatus?.startsWith('error')).length ?? 0;
  const totalRuns    = status?.jobs?.reduce((s, j) => s + j.runCount, 0) ?? 0;
  const activeJobs   = status?.jobs?.length ?? 0;

  /* ── KPI card definitions ── */
  const kpiCards = [
    {
      key: 'stato',
      label: t('admin.automation.kpi.systemStatus'),
      value: loading ? '…' : status?.enabled ? t('admin.automation.statusActive') : t('admin.automation.statusDisabled'),
      icon: <Activity size={22} />,
      accent: status?.enabled ? '#10b981' : '#f59e0b',
      iconBg: status?.enabled ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
    },
    {
      key: 'agenti',
      label: t('admin.automation.kpi.activeAgents'),
      value: activeJobs,
      icon: <Cpu size={22} />,
      accent: '#6366f1',
      iconBg: 'rgba(99,102,241,0.15)',
    },
    {
      key: 'successi',
      label: t('admin.automation.kpi.successRuns'),
      value: successCount,
      icon: <CheckCircle size={22} />,
      accent: '#10b981',
      iconBg: 'rgba(16,185,129,0.15)',
    },
    {
      key: 'errori',
      label: t('admin.automation.kpi.errors'),
      value: errorCount,
      icon: errorCount > 0 ? <XCircle size={22} /> : <CheckCircle size={22} />,
      accent: errorCount > 0 ? '#ef4444' : '#52525b',
      iconBg: errorCount > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(82,82,91,0.15)',
    },
    {
      key: 'totali',
      label: t('admin.automation.kpi.totalRuns'),
      value: totalRuns.toLocaleString('it-IT'),
      icon: <Zap size={22} />,
      accent: '#f97316',
      iconBg: 'rgba(249,115,22,0.15)',
    },
  ];

  /* ── loading skeleton ── */
  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content">
          <div className="auto-loading">
            <RefreshCw size={28} className="auto-spin" />
            <span>{t('admin.automation.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />

      <div className="admin-content">

        {/* ── Page header ── */}
        <div className="content-header">
          <div>
            <h1>{t('admin.automation.title')}</h1>
            <p>{t('admin.automation.subtitle')}</p>
          </div>
          <div className="admin-header-right">
            <LanguageToggle />

            {/* Refresh */}
            <button
              className="admin-btn admin-btn-secondary"
              onClick={fetchStatus}
              title={t('admin.automation.refreshTitle')}
            >
              <RefreshCw size={15} />
              {t('admin.common.refresh')}
            </button>

            {/* Master toggle */}
            <button
              className={`admin-btn ${status?.enabled ? 'admin-btn-danger' : 'admin-btn-primary'}`}
              onClick={toggleAutomation}
              disabled={toggling}
            >
              {status?.enabled
                ? <><ToggleRight size={17} /> {t('admin.automation.disable')}</>
                : <><ToggleLeft  size={17} /> {t('admin.automation.enable')}</>
              }
            </button>
          </div>
        </div>

        {/* ── KPI cards ── */}
        <div className="auto-kpi-grid">
          {kpiCards.map(card => (
            <div
              key={card.key}
              className="stat-card auto-stat-card"
              style={{ borderTop: `3px solid ${card.accent}` }}
            >
              <div
                className="stat-icon"
                style={{ background: card.iconBg, color: card.accent }}
              >
                {card.icon}
              </div>
              <div className="stat-content">
                <p className="stat-label">{card.label}</p>
                <p className="stat-value" style={{ color: card.accent, fontSize: '1.6rem' }}>
                  {card.value}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Auto-refresh indicator ── */}
        <div className="auto-refresh-note">
          <span className="auto-pulse-dot" />
          {t('admin.automation.autoRefresh')}
        </div>

        {/* ── System not started empty state ── */}
        {!status?.started && (
          <div className="auto-empty-state">
            <AlertTriangle size={42} color="#f59e0b" />
            <h3>{t('admin.automation.notStartedTitle')}</h3>
            <p>{t('admin.automation.notStartedText')}</p>
          </div>
        )}

        {/* ── Jobs table ── */}
        {status?.started && (
          <div className="admin-table-container">
            <div className="admin-table-header">
              <h2>
                <Activity size={18} style={{ marginRight: 8, verticalAlign: 'middle', color: '#10b981' }} />
                {t('admin.automation.activeJobs')}
              </h2>
              <span className="auto-job-count">{t('admin.automation.agentsRegistered', { count: activeJobs })}</span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t('admin.automation.colAgent')}</th>
                    <th>{t('admin.automation.colSchedule')}</th>
                    <th>{t('admin.automation.colLastRun')}</th>
                    <th>{t('admin.automation.colStatus')}</th>
                    <th style={{ textAlign: 'right' }}>{t('admin.automation.colRuns')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(status?.jobs ?? []).map(job => {
                    const isError   = job.lastStatus?.startsWith('error');
                    const isSuccess = job.lastStatus === 'success';
                    const isPending = !isError && !isSuccess;

                    const rowAccent = isError ? '#ef4444' : isSuccess ? '#10b981' : '#f59e0b';
                    const badgeBg   = isError ? 'rgba(239,68,68,0.15)' : isSuccess ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)';
                    const badgeColor= isError ? '#ef4444' : isSuccess ? '#10b981' : '#f59e0b';
                    const badgeIcon = isError ? <XCircle size={12} /> : isSuccess ? <CheckCircle size={12} /> : <Clock size={12} />;
                    const badgeText = isError ? t('admin.automation.statusError') : isSuccess ? t('admin.automation.statusOk') : t('admin.automation.statusPending');

                    return (
                      <tr key={job.name} className="auto-job-row" style={{ borderLeft: `3px solid ${rowAccent}` }}>
                        <td className="auto-agent-name">
                          {JOB_LABELS[job.name] || job.name}
                        </td>
                        <td>
                          <code className="auto-schedule">{job.schedule}</code>
                        </td>
                        <td className="auto-lastrun">
                          {job.lastRun
                            ? new Date(job.lastRun).toLocaleString('it-IT')
                            : <span style={{ color: '#52525b' }}>—</span>
                          }
                        </td>
                        <td>
                          <span
                            className="auto-badge"
                            style={{ background: badgeBg, color: badgeColor }}
                          >
                            {badgeIcon}
                            {badgeText}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', color: '#a1a1aa', fontVariantNumeric: 'tabular-nums' }}>
                          {job.runCount.toLocaleString('it-IT')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
